import { erc20Abi, getAddress } from 'viem';
import { getEVMRpcClient } from '../network/getRpcClient';
import { SupportedNetworks } from '../config/networkAddresses';
import { CURRENT_VERSION } from '../config/environment';
import { isNativeToken } from '../../utils/validation/token-validation';
/**
 * Fetches the token balances for a given owner on a specified network.
 * @param network - The network on which the tokens exist.
 * @param tokens - The array of token addresses.
 * @param owner - The owner's address.
 * @returns An array of objects each containing the token address, raw balance, balance, and error message (if any).
 */
export async function fetchEVMTokenBalances({ network, tokens, owner, }) {
    const client = getEVMRpcClient({ chainId: network });
    const results = [];
    const nativeTokenPromises = [];
    const erc20Tokens = [];
    const erc20Calls = [];
    tokens.forEach((token) => {
        let error = '';
        try {
            getAddress(token);
        }
        catch (e) {
            error = `Token address is invalid`;
            results.push({
                network,
                token,
                rawBalance: BigInt(0),
                balance: 0,
                error,
            });
            return;
        }
        try {
            getAddress(owner);
        }
        catch (e) {
            error = `Owner address is invalid`;
            results.push({
                network,
                token,
                rawBalance: BigInt(0),
                balance: 0,
                error,
            });
            return;
        }
        if (isNativeToken(token)) {
            const decimals = SupportedNetworks(CURRENT_VERSION)[network].decimals;
            const nativePromise = client
                .getBalance({ address: owner })
                .then((nativeBalance) => {
                const rawBalance = BigInt(nativeBalance);
                const balance = rawBalance > BigInt(0) ? Number(rawBalance) / 10 ** decimals : 0;
                return { network, token, rawBalance, balance, error: '' };
            })
                .catch((e) => {
                return {
                    network,
                    token,
                    rawBalance: BigInt(0),
                    balance: 0,
                    error: `Error fetching native token balance: ${e.message}`,
                };
            });
            nativeTokenPromises.push(nativePromise);
        }
        else {
            erc20Tokens.push(token);
            erc20Calls.push({
                abi: erc20Abi,
                address: token,
                functionName: 'balanceOf',
                args: [owner],
            }, {
                abi: erc20Abi,
                address: token,
                functionName: 'decimals',
                args: [],
            });
        }
    });
    const erc20Promise = erc20Calls.length > 0
        ? client
            // @ts-ignore
            .multicall({
            contracts: erc20Calls,
            allowFailure: false,
        })
            .then((response) => {
            const erc20Results = [];
            for (let i = 0; i < erc20Tokens.length; i++) {
                const rawBalance = response.shift();
                const decimals = response.shift();
                const balance = rawBalance > BigInt(0) ? Number(rawBalance) / 10 ** decimals : 0;
                erc20Results.push({
                    network,
                    token: erc20Tokens[i],
                    rawBalance,
                    balance,
                    error: '',
                });
            }
            return erc20Results;
        })
            .catch((e) => {
            return erc20Tokens.map((token) => ({
                network,
                token,
                rawBalance: BigInt(0),
                balance: 0,
                error: `Error fetching token balance: ${e.message}`,
            }));
        })
        : Promise.resolve([]);
    const allPromises = Promise.all([Promise.all(nativeTokenPromises), erc20Promise]);
    const [nativeResults, erc20Results] = await allPromises;
    return [...results, ...nativeResults, ...erc20Results];
}
